<template>
  <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-2xl font-bold text-gray-900">Usage Analytics</h1>
      <p class="text-gray-600 mt-1">
        Detailed insights into your spark consumption patterns
      </p>
    </div>

    <!-- Filters -->
    <div class="card mb-8">
      <div class="flex flex-wrap items-center gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Time Period</label
          >
          <select v-model="selectedPeriod" class="input text-sm">
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Service</label
          >
          <select v-model="selectedProduct" class="input text-sm">
            <option value="">All Services</option>
            <option value="scenario_builder">Scenario Builder</option>
            <option value="vidsabio">VidSabio</option>
            <option value="analytics">Learner Analytics</option>
          </select>
        </div>

        <div class="flex-1"></div>

        <button @click="exportData" class="btn-secondary text-sm flex items-center">
          <PhChartBar class="w-4 h-4 mr-1.5" weight="fill" /> Export CSV
        </button>
      </div>
    </div>

    <!-- Stats Overview -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <div class="card text-center">
        <PhTrendDown class="w-6 h-6 mx-auto mb-2 text-red-500" weight="fill" />
        <p class="text-sm text-gray-600">Total Spent</p>
        <p class="text-xl font-bold text-gray-900 font-mono">
          {{ usageStats.totalSpent.toLocaleString() }}
        </p>
      </div>

      <div class="card text-center">
        <PhHash class="w-6 h-6 mx-auto mb-2 text-blue-500" weight="fill" />
        <p class="text-sm text-gray-600">Transactions</p>
        <p class="text-xl font-bold text-gray-900 font-mono">
          {{ usageStats.transactionCount.toLocaleString() }}
        </p>
      </div>

      <div class="card text-center">
        <PhTrendUp class="w-6 h-6 mx-auto mb-2 text-green-500" weight="fill" />
        <p class="text-sm text-gray-600">Daily Average</p>
        <p class="text-xl font-bold text-gray-900 font-mono">
          {{
            Math.round(
              usageStats.totalSpent / Number(selectedPeriod)
            ).toLocaleString()
          }}
        </p>
      </div>

      <div class="card text-center">
        <PhTrophy class="w-6 h-6 mx-auto mb-2 text-purple-500" weight="fill" />
        <p class="text-sm text-gray-600">Top Product</p>
        <p class="text-sm font-bold text-gray-900">
          {{ formatProductName(usageStats.topProduct || "") }}
        </p>
      </div>
    </div>

    <!-- Charts -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
      <!-- Usage Over Time -->
      <div class="card">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">
          Usage Over Time
        </h3>
        <div class="h-64">
          <UsageChart
            :data="filteredChartData"
            :period="Number(selectedPeriod)"
            :loading="sparksStore.isLoading"
          />
        </div>
      </div>

      <!-- Product Breakdown -->
      <div class="card">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">
          Product Breakdown
        </h3>
        <div class="space-y-3">
          <div
            v-for="[product, sparks] in sortedProductUsage"
            :key="product"
            class="flex items-center justify-between"
          >
            <div class="flex items-center space-x-3">
              <component :is="getProductIcon(product)" class="w-5 h-5 text-primary" weight="fill" />
              <span class="font-medium text-gray-900">{{
                formatProductName(product)
              }}</span>
            </div>
            <div class="flex items-center space-x-3">
              <div class="w-24 bg-gray-200 rounded-full h-2">
                <div
                  class="bg-primary h-2 rounded-full"
                  :style="{ width: `${getProductPercentage(sparks)}%` }"
                ></div>
              </div>
              <span class="font-mono text-sm font-medium w-16 text-right">
                {{ sparks.toLocaleString() }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Transaction History -->
    <div class="card">
      <div class="flex items-center justify-between mb-6">
        <h3 class="text-lg font-semibold text-gray-900">Transaction History</h3>
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-600">
            Showing {{ filteredTransactions.length }} of
            {{ sparksStore.transactions.length }} transactions
          </span>
        </div>
      </div>

      <!-- Transaction Table -->
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Date
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Product
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Action
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Sparks
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Balance After
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="transaction in paginatedTransactions"
              :key="transaction.id"
              class="hover:bg-gray-50"
            >
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ formatDate(transaction.created_at) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <component :is="getProductIcon(transaction.product)" class="w-4 h-4 mr-2 text-primary" weight="fill" />
                  <span class="text-sm font-medium text-gray-900">
                    {{ formatProductName(transaction.product) }}
                  </span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ transaction.action }}
              </td>
              <td
                class="px-6 py-4 whitespace-nowrap text-sm font-mono"
                :class="{
                  'text-red-600': transaction.sparks_spent,
                  'text-green-600': transaction.sparks_added,
                }"
              >
                {{ transaction.sparks_spent ? "-" : "+"
                }}{{
                  (
                    transaction.sparks_spent ||
                    transaction.sparks_added ||
                    0
                  ).toLocaleString()
                }}
              </td>
              <td
                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono"
              >
                {{ transaction.balance_after.toLocaleString() }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="mt-6 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
          {{
            Math.min(currentPage * itemsPerPage, filteredTransactions.length)
          }}
          of {{ filteredTransactions.length }} results
        </div>
        <div class="flex space-x-2">
          <button
            @click="currentPage--"
            :disabled="currentPage === 1"
            class="btn-secondary text-sm"
          >
            Previous
          </button>
          <button
            @click="currentPage++"
            :disabled="currentPage === totalPages"
            class="btn-secondary text-sm"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, type Component } from "vue";
import {
  PhChartBar,
  PhTrendDown,
  PhHash,
  PhTrendUp,
  PhTrophy,
  PhMaskHappy,
  PhFilmSlate,
  PhArrowsClockwise,
  PhGear,
  PhSquaresFour,
} from "@phosphor-icons/vue";
import { useSparksStore } from "../stores/sparks";
import { useNotificationStore } from "../stores/notifications";
import UsageChart from "../components/UsageChart.vue";

const sparksStore = useSparksStore();
const notificationStore = useNotificationStore();

// State
const selectedPeriod = ref("30");
const selectedProduct = ref("");
const currentPage = ref(1);
const itemsPerPage = 20;

// Computed
const usageStats = computed(
  () => sparksStore.getUsageStats(Number(selectedPeriod.value)).value
);

const filteredTransactions = computed(() => {
  let transactions = sparksStore.transactions;

  // Filter by time period
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - Number(selectedPeriod.value));
  transactions = transactions.filter((t) => new Date(t.created_at) >= cutoff);

  // Filter by product
  if (selectedProduct.value) {
    transactions = transactions.filter(
      (t) => t.product === selectedProduct.value
    );
  }

  return transactions.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
});

const sortedProductUsage = computed(() => {
  return Object.entries(usageStats.value.productUsage).sort(
    ([, a], [, b]) => b - a
  );
});

const totalPages = computed(() =>
  Math.ceil(filteredTransactions.value.length / itemsPerPage)
);

const paginatedTransactions = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredTransactions.value.slice(start, end);
});

const filteredChartData = computed(() => {
  // Generate chart data based on filtered transactions
  const days = Number(selectedPeriod.value);
  const data = [];

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split("T")[0];

    const dayTransactions = filteredTransactions.value.filter(
      (t) => t.created_at.startsWith(dateStr) && t.sparks_spent
    );

    const sparks_spent = dayTransactions.reduce(
      (sum, t) => sum + (t.sparks_spent || 0),
      0
    );

    data.push({ date: dateStr, sparks_spent });
  }

  return data;
});

// Methods
function formatDate(dateString: string) {
  const date = new Date(dateString);
  return (
    date.toLocaleDateString() +
    " " +
    date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
}

function formatProductName(product: string) {
  const names: Record<string, string> = {
    scenario_builder: "Scenario Builder",
    vidsabio: "VidSabio",
    analytics: "Learner Analytics",
    subscription: "Subscription",
    system: "System",
  };
  return names[product] || product;
}

function getProductIcon(product: string): Component {
  const icons: Record<string, Component> = {
    scenario_builder: PhMaskHappy,
    vidsabio: PhFilmSlate,
    analytics: PhChartBar,
    subscription: PhArrowsClockwise,
    system: PhGear,
  };
  return icons[product] || PhSquaresFour;
}

function getProductPercentage(sparks: number) {
  const total = usageStats.value.totalSpent;
  return total > 0 ? (sparks / total) * 100 : 0;
}

async function exportData() {
  try {
    // Generate CSV data
    const headers = [
      "Date",
      "Product",
      "Action",
      "Sparks Spent",
      "Sparks Added",
      "Balance After",
    ];
    const rows = filteredTransactions.value.map((t) => [
      formatDate(t.created_at),
      formatProductName(t.product),
      t.action,
      t.sparks_spent || 0,
      t.sparks_added || 0,
      t.balance_after,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sparks-usage-${selectedPeriod.value}days-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();
    window.URL.revokeObjectURL(url);

    notificationStore.success(
      "Export Complete",
      "Your usage data has been downloaded."
    );
  } catch (error) {
    notificationStore.error(
      "Export Failed",
      "There was an error exporting your data."
    );
  }
}

// Reset pagination when filters change
function resetPagination() {
  currentPage.value = 1;
}

// Watch for filter changes
watch([selectedPeriod, selectedProduct], resetPagination);

// Lifecycle
onMounted(async () => {
  // Wait for SDK to be initialized before fetching transactions
  if (sparksStore.isInitialized) {
    sparksStore.refreshTransactions(1000); // Get more transactions for analytics
  } else {
    // Watch for SDK initialization
    const stopWatching = watch(
      () => sparksStore.isInitialized,
      (isInitialized) => {
        if (isInitialized) {
          sparksStore.refreshTransactions(1000);
          stopWatching(); // Stop watching once initialized
        }
      },
      { immediate: true }
    );
  }
});
</script>
